/**
 * Company Profile completeness selector (NOT-194).
 *
 * Before this file existed, the Company Profile edit page rendered an
 * inline checklist that omitted `email` and `phone`, even though the form's
 * zod schema (and the customer-facing PDFs that consume the profile) require
 * both. That created a "checklist all green, Save still disabled" state and
 * — because the same gap was used by any caller asking "is the company
 * profile complete?" — drove the NOT-194 onboarding redirect loop: the
 * checklist said done, but the guard said incomplete, so non-settings pages
 * kept being bounced back to /dashboard/settings/company/edit.
 *
 * The fix is to keep the checklist and the "complete?" boolean reading from
 * the SAME single source of truth — this module — so the two sides cannot
 * drift again. Required items mirror the form's zod schema exactly. Optional
 * items are informational (logo / address / business hours).
 */
import type { BusinessHours, CompanyAddress } from '@/types/settings';

export interface CompanyProfileChecklistInput {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  timezone?: string | null;
  logoUrl?: string | null;
  address?: CompanyAddress | null;
  businessHours?: BusinessHours[] | null;
}

export interface CompanyProfileChecklistItem {
  /** Stable id — used for testing + keying. */
  key: 'name' | 'email' | 'phone' | 'timezone' | 'logo' | 'address' | 'businessHours';
  label: string;
  done: boolean;
  /** True for items that don't block "is the profile complete?". */
  optional: boolean;
}

export interface CompanyProfileChecklist {
  items: CompanyProfileChecklistItem[];
  /** True when every NON-optional item is done. Drives onboarding guards. */
  complete: boolean;
}

function nonEmpty(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasAddressInput(address: CompanyAddress | null | undefined): boolean {
  if (!address) return false;
  return Object.values(address).some(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
}

function hasConfiguredBusinessHours(hours: BusinessHours[] | null | undefined): boolean {
  if (!hours?.length) return false;
  return hours.some((hour) => hour.open === true);
}

export function computeCompanyProfileChecklist(
  input: CompanyProfileChecklistInput,
): CompanyProfileChecklist {
  const items: CompanyProfileChecklistItem[] = [
    { key: 'name', label: 'Company name', done: nonEmpty(input.name), optional: false },
    { key: 'email', label: 'Company email', done: nonEmpty(input.email), optional: false },
    { key: 'phone', label: 'Company phone', done: nonEmpty(input.phone), optional: false },
    { key: 'timezone', label: 'Timezone', done: nonEmpty(input.timezone), optional: false },
    { key: 'logo', label: 'Logo', done: nonEmpty(input.logoUrl), optional: true },
    { key: 'address', label: 'Address', done: hasAddressInput(input.address), optional: true },
    {
      key: 'businessHours',
      label: 'Business hours',
      done: hasConfiguredBusinessHours(input.businessHours),
      optional: true,
    },
  ];

  const complete = items.every((item) => item.optional || item.done);

  return { items, complete };
}

export function companyProfileCompletionPercent(checklist: CompanyProfileChecklist): number {
  if (checklist.items.length === 0) return 0;
  const done = checklist.items.filter((item) => item.done).length;
  return Math.round((done / checklist.items.length) * 100);
}

/**
 * Convenience wrapper for callers that only need the boolean (route guards,
 * onboarding banners). Reads from the same selector so the boolean cannot
 * disagree with the visible checklist.
 */
export function isCompanyProfileComplete(input: CompanyProfileChecklistInput): boolean {
  return computeCompanyProfileChecklist(input).complete;
}
